import { makeStyles } from '@mui/styles';
import { neutral4 } from 'apollo-react/colors';
import Grid from 'apollo-react/components/Grid';
import MenuButton from 'apollo-react/components/MenuButton';
import Typography from 'apollo-react/components/Typography';
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import ImportFileModal from './ImportFileModal';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { CREATE_RIGHT } from '../../entity-designer/constants/entity-designer.constants';
import { FORMS_DESIGN_RIGHT } from '../../form-designer/constants/editor.constants';
import { getUserRights } from '../../form-designer/selectors/common.selectors';
import {
  getCreateButtonText,
  isCreateButtonHidden,
  isImportButtonHidden,
  validateXmlMethod,
} from '../../utils/standaloneUtils';
import CreateNewItemDialog from '../CreateNewItemDialog';
import Loader from '../CustomLoader';
import Layout from '../Layout';
import RunSavedSearchDialog from '../RunSavedSearchDialog';

const useStyles = makeStyles({
  headerContainer: {
    padding: '13px 20px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 1,
    letterSpacing: 'normal',
    color: '#000',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  loadingContainer: {
    position: 'absolute',
    width: '95%',
    height: '80%',
  },
  loaderTitle: {
    margin: 35,
    color: neutral4,
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
});

const ItemsListLayout = ({
  dashboardLink,
  isLoading,
  pageTitle,
  children,
  itemName,
  dashboardType,
  importFile,
  runSavedSearchConfig,
  isRunModalOpen,
  setIsRuntModalOpen,
}) => {
  const classes = useStyles();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const translateString = useTranslation();
  const userRights = useSelector(getUserRights);

  const hasFormsDesignRight = useMemo(() => userRights.includes(FORMS_DESIGN_RIGHT), [userRights]);
  const hasEntityCreateRight = useMemo(() => userRights.includes(CREATE_RIGHT), [userRights]);

  const createButtonText = getCreateButtonText(dashboardType);

  const shouldHideImportButton = isImportButtonHidden(dashboardType, hasFormsDesignRight);

  const shouldHideCreateButton = isCreateButtonHidden(
    dashboardType,
    hasFormsDesignRight,
    hasEntityCreateRight
  );

  const handleCreateModalOpen = useCallback(() => {
    setIsCreateModalOpen(!isCreateModalOpen);
  }, [isCreateModalOpen]);

  const handleImportModalOpen = useCallback(() => {
    setIsImportModalOpen(!isImportModalOpen);
  }, [isImportModalOpen]);

  const actionItems = [
    {
      text: translateString(createButtonText),
      onClick: handleCreateModalOpen,
      hidden: shouldHideCreateButton,
    },
    {
      text: translateString('Import'),
      onClick: handleImportModalOpen,
      hidden: shouldHideImportButton,
    },
  ];

  const filteredActions = actionItems.filter((action) => !action.hidden);

  return (
    <Layout dashboardLink={dashboardLink}>
      {isLoading ? (
        <div className={classes.loadingContainer}>
          <Typography variant="h1" className={classes.loaderTitle}>
            {`Loading ${itemName} List...`}
          </Typography>
          <Loader />
        </div>
      ) : (
        <>
          <Grid container className={classes.headerContainer}>
            <Grid item xs={10} className={classes.titleContainer}>
              <Typography className={classes.title}>{pageTitle}</Typography>
            </Grid>
            <Grid item className={classes.buttonWrapper} xs={2}>
              <MenuButton
                buttonText={translateString('Actions')}
                menuItems={filteredActions}
                disabled={filteredActions.length === 0}
              />
            </Grid>
          </Grid>
          {children}
        </>
      )}
      {isCreateModalOpen && (
        <CreateNewItemDialog
          dashboardType={dashboardType}
          isCreateModalOpen={isCreateModalOpen}
          handleCreateModalOpen={handleCreateModalOpen}
        />
      )}
      {isImportModalOpen && (
        <ImportFileModal
          open={isImportModalOpen}
          onClose={handleImportModalOpen}
          dashboardType={dashboardType}
          importFile={importFile}
          validateXml={validateXmlMethod[dashboardType]}
        />
      )}
      {isRunModalOpen && (
        <RunSavedSearchDialog
          runSavedSearchConfig={runSavedSearchConfig}
          setIsRuntModalOpen={setIsRuntModalOpen}
          isRunModalOpen={isRunModalOpen}
        />
      )}
    </Layout>
  );
};

export default ItemsListLayout;
